import { useState, useEffect, useCallback } from "react";
import { 
  Card, 
  DatePicker, 
  Spin, 
  message, 
  Table, 
  Tag, 
  Row, 
  Col, 
  Statistic,
  Typography,
  Space
} from "antd";
import withRoleAccess from "../../hoc/withRoleAccess";
import axios from "axios";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const SalesStatsDashboard = () => {
  const [registrations, setRegistrations] = useState([]);
  const [salesSummary, setSalesSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([
    dayjs().startOf("year"),
    dayjs().endOf("year"),
  ]);

  const API_URL = process.env.REACT_APP_API_URL;

  const fetchData = useCallback(
    async (startDate, endDate) => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");

        // Fetch registrations by salesperson
        const regRes = await axios.get(
          `${API_URL}/sales-persons/registrations/stats-by-sales`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { startDate, endDate },
          }
        );

        // Fetch sales summary by salesperson
        const salesRes = await axios.get(
          `${API_URL}/sales-persons/class-groups/sales-summary`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { startDate, endDate },
          }
        );

        setRegistrations(regRes.data);
        setSalesSummary(salesRes.data);
      } catch (err) {
        console.error("Error fetching stats:", err);
        message.error("Failed to fetch sales stats");
      } finally {
        setLoading(false);
      }
    },
    [API_URL]
  );

  useEffect(() => {
    fetchData(
      dateRange[0].format("YYYY-MM-DD"),
      dateRange[1].format("YYYY-MM-DD")
    );
  }, [dateRange, fetchData]);

  // Calculate overall summary statistics
  const overallStats = {
    totalSales: salesSummary.reduce((sum, sp) => sum + sp.totalSales, 0),
    totalHours: salesSummary.reduce((sum, sp) => sum + sp.totalHours, 0),
    taughtHours: salesSummary.reduce((sum, sp) => sum + sp.taughtHours, 0),
    remainingHours: salesSummary.reduce((sum, sp) => sum + sp.remainingHours, 0),
    totalStudents: registrations.reduce((sum, sp) => 
      sum + Object.values(sp.months).reduce((a, b) => a + b, 0), 0
    ),
    totalSalespersons: salesSummary.length
  };

  // Merge data for comprehensive table
  const combinedData = salesSummary.map(sp => {
    const regData = registrations.find(r => 
      r.salesperson_id === sp.salesperson.id
    );
    
    const totalStudents = regData 
      ? Object.values(regData.months).reduce((a, b) => a + b, 0) 
      : 0;

    const monthlyBreakdown = regData ? regData.months : {};

    return {
      ...sp,
      totalStudentsRegistered: totalStudents,
      monthlyRegistrations: monthlyBreakdown,
      avgSalesPerStudent: totalStudents > 0 ? sp.totalSales / totalStudents : 0,
      hoursCompletionRate: sp.totalHours > 0 ? (sp.taughtHours / sp.totalHours) * 100 : 0
    };
  });

  // Main table columns
  const mainColumns = [
    {
      title: "Salesperson",
      dataIndex: ["salesperson"],
      key: "salesperson",
      width: 200,
      fixed: 'left',
      render: (sp) => (
        <div>
          <div><strong>{sp.name || 'Unknown'}</strong></div>
          {sp.nickname && <Tag color="blue" size="small">{sp.nickname}</Tag>}
          {sp.phone && <div><Text type="secondary" style={{fontSize: '12px'}}>{sp.phone}</Text></div>}
        </div>
      ),
    },
    {
      title: "Sales Revenue",
      children: [
        {
          title: "Total Sales (THB)",
          dataIndex: "totalSales",
          key: "totalSales",
          width: 130,
          render: (val) => (
            <Statistic 
              value={val} 
              precision={2} 
              valueStyle={{ fontSize: '14px' }}
              formatter={(value) => `${parseFloat(value).toLocaleString()}`}
            />
          ),
          sorter: (a, b) => a.totalSales - b.totalSales,
        },
        {
          title: "Avg per Student",
          dataIndex: "avgSalesPerStudent",
          key: "avgSalesPerStudent", 
          width: 120,
          render: (val) => (
            <Text strong style={{ color: '#52c41a' }}>
              {parseFloat(val).toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </Text>
          ),
          sorter: (a, b) => a.avgSalesPerStudent - b.avgSalesPerStudent,
        }
      ]
    },
    {
      title: "Teaching Hours",
      children: [
        {
          title: "Total",
          dataIndex: "totalHours",
          key: "totalHours",
          width: 80,
          render: (val) => <Text>{val.toFixed(2)}</Text>,
          sorter: (a, b) => a.totalHours - b.totalHours,
        },
        {
          title: "Taught",
          dataIndex: "taughtHours",
          key: "taughtHours",
          width: 80,
          render: (val) => <Text style={{color: '#52c41a'}}>{val.toFixed(2)}</Text>,
          sorter: (a, b) => a.taughtHours - b.taughtHours,
        },
        {
          title: "Remaining",
          dataIndex: "remainingHours",
          key: "remainingHours",
          width: 90,
          render: (val) => <Text style={{color: '#fa8c16'}}>{val.toFixed(2)}</Text>,
          sorter: (a, b) => a.remainingHours - b.remainingHours,
        },
        {
          title: "Progress",
          dataIndex: "hoursCompletionRate",
          key: "progress",
          width: 80,
          render: (val) => (
            <Tag color={val >= 75 ? 'green' : val >= 50 ? 'orange' : 'red'}>
              {val.toFixed(0)}%
            </Tag>
          ),
          sorter: (a, b) => a.hoursCompletionRate - b.hoursCompletionRate,
        }
      ]
    },
    {
      title: "Classes & Courses",
      children: [
        {
          title: "Total Classes",
          dataIndex: "totalClasses",
          key: "totalClasses",
          width: 100,
          render: (val) => <Text>{val}</Text>,
          sorter: (a, b) => a.totalClasses - b.totalClasses,
        },
        {
          title: "Active Courses",
          dataIndex: "activeCourses", 
          key: "activeCourses",
          width: 110,
          render: (val) => <Tag color="purple">{val}</Tag>,
          sorter: (a, b) => a.activeCourses - b.activeCourses,
        }
      ]
    },
    {
      title: "Student Registrations",
      children: [
        {
          title: "Total Students",
          dataIndex: "totalStudentsRegistered",
          key: "totalStudents",
          width: 110,
          render: (val) => <Tag color="green" style={{fontSize: '13px'}}>{val}</Tag>,
          sorter: (a, b) => a.totalStudentsRegistered - b.totalStudentsRegistered,
        },
        {
          title: "Monthly Breakdown",
          dataIndex: "monthlyRegistrations",
          key: "monthlyBreakdown",
          width: 180,
          render: (months) => (
            <Space size={4} wrap>
              {Object.entries(months).map(([month, count]) => (
                <Tag key={month} size="small" color="blue">
                  {month}: {count}
                </Tag>
              ))}
            </Space>
          ),
        }
      ]
    }
  ];

  return (
    <div>
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Student & Course Summary
            </Title>
            <Text type="secondary">
              Comprehensive sales performance for new students and new courses
            </Text>
          </Col>
          <Col>
            <Space>
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={(val) => setDateRange(val)}
                allowClear={false}
                style={{ minWidth: 250 }}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Summary Statistics */}
      <Card title="Overall Summary" style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col span={4}>
            <Statistic
              title="Total Sales Revenue"
              value={overallStats.totalSales}
              precision={2}
              suffix="THB"
              valueStyle={{ color: '#1890ff' }}
              formatter={(value) => parseFloat(value).toLocaleString()}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Total Students"
              value={overallStats.totalStudents}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Total Teaching Hours"
              value={overallStats.totalHours}
              precision={2}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Hours Taught"
              value={overallStats.taughtHours}
              precision={2}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Remaining Hours"
              value={overallStats.remainingHours}
              precision={2}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Col>
          <Col span={4}>
            <Statistic
              title="Active Salespersons"
              value={overallStats.totalSalespersons}
              valueStyle={{ color: '#13c2c2' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Main Data Table */}
      <Card title="Detailed Sales & Registration Report" style={{ marginBottom: 24 }}>
        <Spin spinning={loading}>
          <Table
            columns={mainColumns}
            dataSource={combinedData}
            rowKey={(record) => record.salesperson.id || record.salesperson.name}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} salespersons`
            }}
            bordered
            scroll={{ x: 1400 }}
            size="middle"
          />
        </Spin>
      </Card>


    </div>
  );
};

export default withRoleAccess(SalesStatsDashboard, ["superadmin"]);